/**
 * Shared "quick add to cart" AJAX helper.
 *
 * Talks to Theme\App\Woocommerce\QuickAddToCart's `db_quick_add_to_cart`
 * endpoint — NOT WooCommerce's native `wc-ajax=add_to_cart` endpoint, which
 * silently ignores `variation_id` / `attribute_*` fields and can never add a
 * variable (color/size) product to the cart. See that class for details.
 *
 * Used by: product-grid.js, product-carousel (via product-grid),
 * product-bundle-showcase.js and sticky-add-to-cart.js, so every "quick add"
 * control on the site shares one correct, secured implementation.
 */

/**
 * @return {{addToCartUrl?: string, addToCartAction?: string, nonce?: string, i18n?: Record<string, string>}}
 */
export function getCartAjaxConfig() {
	return window.dbProductGrid || {};
}

/**
 * @param {{productId: number, variationId?: number, quantity?: number, attributes?: Record<string, string>}} params
 * @return {Promise<{success: boolean, message?: string, data?: object}>}
 */
export async function ajaxAddToCart( { productId, variationId = 0, quantity = 1, attributes = {} } ) {
	const config = getCartAjaxConfig();

	if ( ! config.addToCartUrl ) {
		return { success: false, message: config.i18n?.error || '' };
	}

	const body = new URLSearchParams( {
		action:     config.addToCartAction || 'db_quick_add_to_cart',
		nonce:      config.nonce || '',
		product_id: productId,
		quantity,
	} );

	if ( variationId ) {
		body.set( 'variation_id', variationId );
	}
	Object.entries( attributes ).forEach( ( [ key, value ] ) => body.set( key, value ) );

	try {
		const res  = await fetch( config.addToCartUrl, {
			method:  'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body:    body.toString(),
		} );
		const json = await res.json();

		if ( ! json.success ) {
			return { success: false, message: json.data?.message || config.i18n?.error || '' };
		}

		applyCartFragments( json.data?.fragments );

		return { success: true, data: json.data };
	} catch {
		return { success: false, message: config.i18n?.error || '' };
	}
}

/**
 * Replace every element matching a WooCommerce cart-fragment selector with
 * its freshly rendered markup (mini-cart, cart count badge, etc.).
 *
 * @param {Record<string, string>|undefined} fragments
 */
export function applyCartFragments( fragments ) {
	if ( ! fragments || typeof fragments !== 'object' ) return;

	Object.entries( fragments ).forEach( ( [ selector, html ] ) => {
		document.querySelectorAll( selector ).forEach( ( el ) => {
			el.outerHTML = html;
		} );
	} );
}

